// Client-portal view of the purchasable modules. Slugs, labels and prices come
// from @cms/config (the single source of truth that also drives the
// GoCardless subscription amounts); only the portal hrefs live here.
import { MODULE_DEFINITIONS } from "@cms/config";

export interface ModuleCatalogueEntry {
  slug: string;
  label: string;
  href: string;
  monthly_pence: number;
  description: string;
}

export interface ModuleNavItem {
  /** The owning module — nav shows only when this module is active. */
  slug: string;
  /** Unique key for icons and React keys (a module may have several entries). */
  key: string;
  label: string;
  href: string;
}

/** Portal route each module's panel lives under. */
const MODULE_HREFS: Record<string, string> = {
  restaurant_menu: "/menu",
  booking: "/bookings",
  posts: "/blog",
  events: "/events",
  sla: "/support",
};

/**
 * The purchasable module catalogue. Module slugs map to optional CMS panels:
 * panels stay fully hidden unless the module is active for the tenant —
 * `GET /tenant/status` returns `active_modules` and the sidebar renders
 * matches automatically. Inactive modules appear only on the Add-ons upsell
 * page, which is a sales touchpoint (no self-serve purchase): the client asks
 * by email and the founder activates via the admin panel.
 */
export const MODULE_CATALOGUE: ModuleCatalogueEntry[] = MODULE_DEFINITIONS.map((def) => ({
  slug: def.slug,
  label: def.label,
  href: MODULE_HREFS[def.slug] ?? "/modules",
  monthly_pence: def.monthly_pence,
  description: def.description,
}));

// One sidebar entry per module. The menu module's single "Menu" page hosts
// its own Menus / Items / QR codes tabs, so it needs just one nav item.
export const MODULE_NAV: ModuleNavItem[] = MODULE_CATALOGUE.map((entry) => ({
  slug: entry.slug,
  key: entry.slug,
  label: entry.label,
  href: entry.href,
}));

export function activeModuleNav(activeModules: string[] | undefined | null): ModuleNavItem[] {
  if (!Array.isArray(activeModules)) return [];
  return MODULE_NAV.filter((item) => activeModules.includes(item.slug));
}
